import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InputAmount } from '@components/atoms/InputAmount';
import { Button, ButtonMode } from '@components/atoms/Button';
import { useNotifications } from '@hooks/useNotifications';
import { useAddProductToCart } from '@services/useAddProductToCart';

export interface Product {
    uuid: string;
    attributesValuesUuids: string[];
    name: string;
    image: string | null;
    quantity?: number;
}

export interface AddToCartProps {
    disabled: boolean;
    products: Product[];
}

export const AddToCart: FC<AddToCartProps> = ({ products, disabled }) => {
    const { request: requestAddProductToCart, loading, lastSuccessfulResponseTime } = useAddProductToCart();
    const { t } = useTranslation();
    const { showProductAddedToCartNotification } = useNotifications();

    const translations = {
        addOneToCart: t('add-to-cart-component.add-to-cart-single'),
        feedbackAddedOneToCart: t('add-to-cart-component.feedback.added-to-cart-single'),
        addToCart: t('add-to-cart-component.add-to-cart-multiple'),
        feedbackAddedToCart: t('add-to-cart-component.feedback.added-to-cart-multiple'),
    };

    const [quantities, setQuantities] = useState<Record<string, number>>({});

    useEffect(() => {
        setQuantities(products.reduce((acc, product) => ({ ...acc, [product.uuid]: product.quantity || 1 }), {}));
    }, [products]);

    const [notificationShown, setNotificationShown] = useState(false);

    useEffect(() => {
        if (lastSuccessfulResponseTime !== null && !notificationShown) {
            const productList = products.map((product) => ({
                name: product.name,
                image: product.image || '',
                quantity: product.quantity,
            }));

            const message = products.length > 1 ? translations.feedbackAddedToCart : translations.feedbackAddedOneToCart;

            showProductAddedToCartNotification({
                message: message,
                product: productList,
            });
            setNotificationShown(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastSuccessfulResponseTime, notificationShown]);

    const handleAddToCart = () => {
        setNotificationShown(false);
        products.forEach((product) => {
            requestAddProductToCart({
                product_uuid: product.uuid,
                quantity: quantities[product.uuid],
                attributes_values_uuids: product.attributesValuesUuids,
            });
        });
    };

    return (
        <div className="d-flex flex-column gap-3" data-testid="add-to-cart">
            {products.length === 1 ? (
                <div key={products[0].uuid} className="d-flex align-items-center gap-2">
                    <InputAmount
                        min={1}
                        value={quantities[products[0].uuid]}
                        onChange={(value) => setQuantities({ ...quantities, [products[0].uuid]: value })}
                        disabled={disabled || loading}
                    />
                    <Button
                        mode={ButtonMode.primary}
                        icon="fa-regular fa-basket-shopping"
                        label={translations.addOneToCart}
                        disabled={disabled || loading}
                        onClick={handleAddToCart}
                    />
                </div>
            ) : (
                <div className="d-flex align-items-center gap-2">
                    <Button
                        mode={ButtonMode.primary}
                        icon="fa-regular fa-basket-shopping"
                        label={translations.addToCart}
                        disabled={disabled || loading}
                        onClick={handleAddToCart}
                    />
                </div>
            )}
        </div>
    );
};
